import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList, CheckCircle2, ArrowRight, Sparkles, Package,
  Mailbox, Truck, CreditCard, Star, Pin,
} from "lucide-react";
import { notificationsAPI } from "../api";
import { useAuth } from "../context/AuthContext";
import "./ActionsList.css";

const ACTION_ICONS = {
  "🆕": Sparkles,
  "📦": Package,
  "📮": Mailbox,
  "🚚": Truck,
  "💳": CreditCard,
  "✅": CheckCircle2,
  "⭐": Star,
};

const ActionIcon = ({ icon }) => {
  const Icon = ACTION_ICONS[icon] || Pin;
  return <Icon size={22} strokeWidth={2} />;
};

const ActionsList = () => {
  const { isAuthenticated } = useAuth();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const loadActions = async () => {
      try {
        setLoading(true);
        const response = await notificationsAPI.getActions();
        setActions(response.data.actions || []);
        setError("");
      } catch (err) {
        console.error("Error loading actions:", err);
        setError("Erreur lors du chargement des actions");
      } finally {
        setLoading(false);
      }
    };

    loadActions();
    const interval = setInterval(loadActions, 30000); // Rafraîchir toutes les 30 secondes

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="actions-list-container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="actions-list-container">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="actions-list-container">
        <div className="actions-header">
          <h2 className="icon-title"><ClipboardList size={22} strokeWidth={2.25} /> Actions à réaliser</h2>
        </div>
        <div className="actions-empty">
          <div className="empty-state-icon"><CheckCircle2 size={56} strokeWidth={1.75} /></div>
          <p>Aucune action en attente</p>
          <p className="text-muted">Tout est à jour !</p>
        </div>
      </div>
    );
  }

  // Grouper les actions par priorité
  const highPriorityActions = actions.filter(a => a.priority === "high");
  const normalPriorityActions = actions.filter(a => a.priority === "normal");
  const lowPriorityActions = actions.filter(a => a.priority === "low");

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "high":
        return "action-high";
      case "normal":
        return "action-normal";
      case "low":
        return "action-low";
      default:
        return "";
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "high":
        return "Urgent";
      case "normal":
        return "Normal";
      case "low":
        return "Faible";
      default:
        return "";
    }
  };

  return (
    <div className="actions-list-container">
      <div className="actions-header">
        <h2 className="icon-title"><ClipboardList size={22} strokeWidth={2.25} /> Actions à réaliser</h2>
        <span className="actions-count">{actions.length}</span>
      </div>

      <div className="actions-list">
        {highPriorityActions.length > 0 && (
          <div className="actions-group">
            <h3 className="actions-group-title priority-high">
              <span className="priority-dot" /> Priorité haute ({highPriorityActions.length})
            </h3>
            {highPriorityActions.map((action) => (
              <Link
                key={`${action.action_type}-${action.order_id}`}
                to={action.link || `/orders/${action.order_id}`}
                className={`action-card ${getPriorityClass(action.priority)}`}
              >
                <div className="action-icon"><ActionIcon icon={action.icon} /></div>
                <div className="action-content">
                  <div className="action-header">
                    <h4 className="action-title">{action.title}</h4>
                    <span className="action-priority-badge priority-high">
                      {getPriorityLabel(action.priority)}
                    </span>
                  </div>
                  <p className="action-description">{action.description}</p>
                </div>
                <div className="action-arrow"><ArrowRight size={18} strokeWidth={2.25} /></div>
              </Link>
            ))}
          </div>
        )}

        {normalPriorityActions.length > 0 && (
          <div className="actions-group">
            <h3 className="actions-group-title priority-normal">
              <span className="priority-dot" /> Priorité normale ({normalPriorityActions.length})
            </h3>
            {normalPriorityActions.map((action) => (
              <Link
                key={`${action.action_type}-${action.order_id}`}
                to={action.link || `/orders/${action.order_id}`}
                className={`action-card ${getPriorityClass(action.priority)}`}
              >
                <div className="action-icon"><ActionIcon icon={action.icon} /></div>
                <div className="action-content">
                  <div className="action-header">
                    <h4 className="action-title">{action.title}</h4>
                    <span className="action-priority-badge priority-normal">
                      {getPriorityLabel(action.priority)}
                    </span>
                  </div>
                  <p className="action-description">{action.description}</p>
                </div>
                <div className="action-arrow"><ArrowRight size={18} strokeWidth={2.25} /></div>
              </Link>
            ))}
          </div>
        )}

        {lowPriorityActions.length > 0 && (
          <div className="actions-group">
            <h3 className="actions-group-title priority-low">
              <span className="priority-dot" /> Priorité faible ({lowPriorityActions.length})
            </h3>
            {lowPriorityActions.map((action) => (
              <Link
                key={`${action.action_type}-${action.order_id}`}
                to={action.link || `/orders/${action.order_id}`}
                className={`action-card ${getPriorityClass(action.priority)}`}
              >
                <div className="action-icon"><ActionIcon icon={action.icon} /></div>
                <div className="action-content">
                  <div className="action-header">
                    <h4 className="action-title">{action.title}</h4>
                    <span className="action-priority-badge priority-low">
                      {getPriorityLabel(action.priority)}
                    </span>
                  </div>
                  <p className="action-description">{action.description}</p>
                </div>
                <div className="action-arrow"><ArrowRight size={18} strokeWidth={2.25} /></div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionsList;

